import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Boxes,
  Plug,
  Building2,
  MonitorSmartphone,
  Layers,
  Stethoscope,
  Sparkles,
  Brain,
  ShieldAlert,
  Workflow,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   Group 1 — Growth & Distribution (light cream cards, 2×2)
   ───────────────────────────────────────────────────────────────────────── */
const growth = [
  {
    Icon: Boxes,
    title: "Retail under Group (RUG)",
    body:
      "Launch and scale byte-sized insurance products with high-volume, low-latency issuance.",
  },
  {
    Icon: Plug,
    title: "Affinity & Embedded Insurance",
    body:
      "Integrate seamlessly with ecosystem partners across digital platforms for embedded distribution.",
  },
  {
    Icon: Building2,
    title: "Bancassurance",
    body:
      "Enable deep bank integrations for frictionless policy issuance and servicing.",
  },
  {
    Icon: MonitorSmartphone,
    title: "Digital Direct",
    body:
      "Deliver end-to-end digital journeys — from quote to claims — with minimal operational overhead.",
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Group 2 — Core Transformation (blue-tint, two full-width)
   ───────────────────────────────────────────────────────────────────────── */
const core = [
  {
    Icon: Layers,
    title: "Group Core Modernization",
    body:
      "Replace legacy systems with a unified core platform — reducing cost, improving control and accelerating product launches.",
  },
  {
    Icon: Stethoscope,
    title: "Multi-Product Group Administration",
    body:
      "Manage life, health, credit and pension products on a single platform with unified policy, accounting and reporting.",
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Group 3 — AI-Led Operations (dark navy block, 2×2 indigo cards)
   ───────────────────────────────────────────────────────────────────────── */
const ai = [
  {
    Icon: Sparkles,
    title: "AI-Driven Claims Automation",
    body: "Accelerate claims processing with intelligent triaging and decision support.",
  },
  {
    Icon: Brain,
    title: "Underwriting Intelligence",
    body: "Enhance underwriting with dynamic risk scoring and decision models.",
  },
  {
    Icon: ShieldAlert,
    title: "Fraud Detection & Prevention",
    body: "Identify anomalies in real time and reduce claims leakage.",
  },
  {
    Icon: Workflow,
    title: "Operational Automation",
    body: "Drive efficiency through AI-led workflow orchestration and exception handling.",
  },
];

const GroupLabel = ({ children, tone = "dark" }) => (
  <div className="mb-5">
    <span className={`overline ${tone === "light" ? "text-white/70" : "text-slate-500"}`}>
      {children}
    </span>
  </div>
);

export const UseCases = () => {
  return (
    <section id="use-cases" data-testid="use-cases-section" className="bg-white">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14 md:mb-16 max-w-3xl mx-auto"
        >
          <div className="mb-6">
            <span className="overline text-copper">Use Cases</span>
          </div>
          <h2
            data-testid="use-cases-title"
            className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] tracking-tight leading-[1.1] text-ink mb-5"
          >
            Built for{" "}
            <span className="text-copper">High-Growth</span> Insurance Models
          </h2>
          <p className="text-base md:text-[18px] text-slate-600 leading-relaxed max-w-[800px] mx-auto">
            From growth and distribution to core transformation and AI-led
            operations, Gradatim powers the full spectrum of modern insurance
            models.
          </p>
        </motion.div>

        {/* GROUP 1 — Growth & Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <GroupLabel>Growth &amp; Distribution</GroupLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {growth.map((c, i) => (
              <motion.article
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.55 }}
                className="group bg-cream border border-hairline rounded-2xl p-5 md:p-6 min-h-[140px] flex items-start gap-5 hover:border-copper/40 hover:bg-white transition-colors"
                data-testid={`growth-card-${i}`}
              >
                <div className="h-11 w-11 shrink-0 bg-white border border-hairline group-hover:border-copper flex items-center justify-center transition-colors">
                  <c.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-[17px] md:text-[18px] tracking-tight text-ink leading-snug mb-1.5">
                    {c.title}
                  </h3>
                  <p className="text-[13px] md:text-[14px] text-slate-600 leading-relaxed">
                    {c.body}
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-copper group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
              </motion.article>
            ))}
          </div>
        </motion.div>

        {/* GROUP 2 — Core Transformation */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <GroupLabel>Core Transformation</GroupLabel>
          <div className="grid grid-cols-1 gap-4">
            {core.map((c, i) => (
              <motion.article
                key={c.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                className="group rounded-2xl border border-blue-100 p-5 md:p-7 min-h-[110px] flex items-center gap-6 hover:border-copper/40 transition-colors"
                style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #F5F7FA 100%)" }}
                data-testid={`core-card-${i}`}
              >
                <div className="h-12 w-12 shrink-0 bg-white border border-blue-100 group-hover:border-copper flex items-center justify-center transition-colors">
                  <c.Icon className="h-5 w-5 text-[#1E3A8A]" strokeWidth={1.6} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-[19px] md:text-[22px] tracking-tight text-ink leading-snug mb-1.5">
                    {c.title}
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-slate-700 leading-relaxed max-w-3xl">
                    {c.body}
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-copper group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0 hidden md:block" />
              </motion.article>
            ))}
          </div>
        </motion.div>

        {/* GROUP 3 — AI-Led Operations (dark navy block) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative rounded-[20px] p-6 md:p-8 mt-8 overflow-hidden grain-overlay"
          style={{ background: "#0B1F3A" }}
          data-testid="ai-ops-block"
        >
          {/* glow */}
          <div aria-hidden className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(200,75,49,0.18)" }} />
          <div aria-hidden className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(56,189,248,0.10)" }} />
          {/* faint grid */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />

          <div className="relative flex items-center justify-between mb-6 md:mb-8">
            <GroupLabel tone="light">AI-Led Operations</GroupLabel>
            <span className="hidden md:inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase text-white/70">
              <span className="h-1.5 w-1.5 bg-copper rounded-full" />
              Intelligence Layer
            </span>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4">
            {ai.map((c, i) => (
              <motion.article
                key={c.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ delay: 0.1 + i * 0.07, duration: 0.55 }}
                className="group rounded-[14px] p-5 md:p-6 min-h-[130px] flex items-start gap-5 hover:bg-[#1E3A8A]/80 transition-colors"
                style={{ background: "#1E3A8A" }}
                data-testid={`ai-card-${i}`}
              >
                <div className="h-11 w-11 shrink-0 bg-white/10 border border-white/15 group-hover:border-copper flex items-center justify-center transition-colors">
                  <c.Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-[17px] md:text-[19px] tracking-tight text-white leading-snug mb-1.5">
                    {c.title}
                  </h3>
                  <p className="text-[13px] md:text-[14px] text-slate-300 leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
